import { _isEmpty, _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash.ts';
import { SpeciesV1 } from '@/model/Species.model.ts';
import type { GroupApiId, TreatmentGroup } from '@/model/TreatmentGroup.model';
import { MetadataField, type MetadataFieldTypes, MetadataFieldWithValue } from '@/model/Metadata.model.ts';
import { metadataToColumnSettingMapper } from '@/components/CreateTreatmentGroups/CreateTreatmentGroups.utils.ts';
import type Handsontable from 'handsontable';
import { DateUtils } from '@/utils/Date.utils';
import { isAfter } from 'date-fns';
import { updateStrainSelectDropdown } from '@/components/Colony/Colony.utils.ts';
import type { CustomGridSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import {
  AnimalDefinitionApiId,
  PlannedAnimal,
  PlannedAnimalData,
  PlanTypeMap,
  PlanTypes,
} from '@/model/PlannedAnimal.model.ts';
import { Nullable, UUID } from '@/model/Common.model.ts';
import { changeApiIdPrefix, generateTempApiId, isTempApiId } from '@/utils/ApiId.utils.ts';
import { StrainApiId } from '@/model/Strain.model.ts';
import { TempAnimalApiId, TempAnimalDefinitionApiId } from '@/model/SamplePlan.model';
import { AnimalApiId } from '@/model/Animal.model';
import { PlannedAnimalDefinition } from '@/components/Animals/PlannedAnimals/PlannedAnimals.utils.tsx';
import { MultiSelectEditor } from '@/components/UI/SpreadSheet/editors/MultiSelectEditor';
import { BaseRenderer } from 'handsontable/renderers/base';
import Core from 'handsontable/core';
import { CellProperties } from 'handsontable/settings';
import { renderToStaticMarkup } from 'react-dom/server';
import { GroupCell, GroupOption } from '@/components/Studies/Create/Steps/Animals/GroupCell';

export interface AnimalRow {
  apiId: AnimalDefinitionApiId | TempAnimalDefinitionApiId | AnimalApiId | TempAnimalApiId;
  plannedId: string;
  groupIdentifier: Nullable<UUID | GroupApiId>;
  sex: string;
  dob?: string;
  species?: string;
  strain?: string;
  metadata?: { [key: string]: MetadataFieldWithValue };
  isMapped?: boolean;
}

export const generateInitialAnimals = (groups: Array<TreatmentGroup>, dobRequired: boolean): Array<AnimalRow> => {
  const animalData: Array<AnimalRow> = [];
  groups.forEach((group, index) => {
    const maxSubjects = group.max_subjects;
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (maxSubjects > 0 && group.name) {
      for (let i = 1; i <= maxSubjects; i++) {
        animalData.push({
          apiId: generateTempApiId('adf'),
          plannedId: `${index + 1}_${i}`,
          groupIdentifier: group.temp_id as UUID,
          sex: '',
          dob: dobRequired ? DateUtils.dateNow() : '',
          species: '',
          strain: '',
          metadata: {},
        });
      }
    }
  });
  return animalData;
};

export const generateAnimalColumns = (
  animals: Array<PlannedAnimal>,
  groups: Array<TreatmentGroup>,
  planType: PlanTypes,
  dobRequired: boolean = true
): Array<AnimalRow> => {
  const animalData: Array<AnimalRow> = [];
  if (planType === PlanTypeMap.skip) {
    return animalData;
  }
  animals.forEach((animal) => {
    const plannedGroup = groups.find((group) => group.temp_id === animal.group_temp_id);
    animalData.push({
      apiId:
        _notNil(animal.temp_id) && isTempApiId(planType === PlanTypeMap.planned ? 'adf' : 'aml', animal.temp_id)
          ? animal.temp_id
          : planType === PlanTypeMap.planned
            ? generateTempApiId('adf')
            : generateTempApiId('aml'),
      plannedId: animal.name,
      groupIdentifier: plannedGroup?.temp_id as UUID,
      sex: animal.sex,
      dob: animal?.dob ?? (dobRequired ? DateUtils.dateNow() : ''),
      species: animal.species,
      strain: animal.strain,
      metadata: animal.metadata,
    });
  });
  return animalData;
};

export const createPlannedAnimalData = (
  animals: Array<PlannedAnimal>,
  planType: PlanTypes,
  animalsPerCage: number | null = null
): PlannedAnimalData => {
  switch (planType) {
    case PlanTypeMap.planned:
      return {
        type: planType,
        animals,
      };
    case PlanTypeMap.registered:
      return {
        type: planType,
        animals,
        animalsPerCage: animalsPerCage ?? 5,
      };
    case PlanTypeMap.skip:
      return {
        type: planType,
      };
  }
};

export const convertToPlannedAnimals = (
  animalData: Array<AnimalRow>,
  groups: Array<TreatmentGroup>,
  species: Array<SpeciesV1>,
  animalsPerCage: number | null,
  planType: PlanTypes,
  groupIdAccessor: (group: TreatmentGroup) => string
) => {
  const plannedAnimals: Array<PlannedAnimal> = animalData.map((animal) => {
    const plannedGroup = groups.find((group) => groupIdAccessor(group) === animal.groupIdentifier);
    const strainApiId =
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      species.find((s) => s.name === animal.species)?.strains?.find((strain) => strain.name === animal.strain)
        ?.api_id || '';
    return {
      temp_id:
        _notNil(animal.apiId) && isTempApiId(planType === PlanTypeMap.planned ? 'adf' : 'aml', animal.apiId)
          ? animal.apiId
          : planType === PlanTypeMap.planned
            ? generateTempApiId('adf')
            : generateTempApiId('aml'),
      name: animal.plannedId,
      group_temp_id: plannedGroup?.temp_id,
      sex: animal.sex,
      dob: animal?.dob ?? '',
      species: animal?.species,
      strain: animal?.strain,
      strainApiId: strainApiId as StrainApiId,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      metadata: animal.metadata || {},
    };
  });

  return createPlannedAnimalData(plannedAnimals, planType, animalsPerCage);
};

export const updateTmpIds = (animals: Array<AnimalRow>, planType: PlanTypes): Array<AnimalRow> => {
  return animals.map((animal) => ({
    ...animal,
    apiId: changeApiIdPrefix(animal.apiId, planType === PlanTypeMap.planned ? 'adf' : 'aml', true),
  }));
};

export const baseColHeaders = {
  names: ['Planned animal ID', 'Group', 'Sex (req.)', 'Species', 'Strain'],
  ids: ['plannedId', 'groupName', 'sex', 'species', 'strain'],
};

export const baseColHeadersWithDob = {
  names: ['Name', 'Group', 'Sex (req.)', 'DOB (req.)', 'Species', 'Strain'],
  ids: ['plannedId', 'groupName', 'sex', 'dob', 'species', 'strain'],
};

export const baseColHeadersWithOptionalDob = {
  names: ['Name', 'Group', 'Sex (req.)', 'DOB', 'Species', 'Strain'],
  ids: ['plannedId', 'groupName', 'sex', 'dob', 'species', 'strain'],
};

export const validatePlannedAnimalId = (
  value: string,
  unselectedRows: Array<PlannedAnimalDefinition>,
  callback: (valid: boolean) => void,
  hot: Nullable<Handsontable>,
  required: boolean = true
) => {
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if ((!value || value.length < 1) && required) {
    return callback(false);
  }
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (hot) {
    const currentData = hot.getData() as Array<Array<any>>;
    const idIndex = hot.propToCol('plannedId');
    const duplicateIDs =
      currentData.filter((row) => _notNil(value) && row[idIndex] === value).length +
      unselectedRows.filter((row) => row.planned_id === value).length;
    return callback(duplicateIDs <= 1);
  }
  return callback(false);
};

export const validateGroupName = (
  value: string,
  unselectedRows: Array<PlannedAnimalDefinition>,
  callback: (valid: boolean) => void,
  hot: Handsontable | null,
  groups: Array<TreatmentGroup>,
  planType: PlanTypes,
  groupIdAccessor: (group: TreatmentGroup) => string
) => {
  if (_isEmpty(value) && planType === PlanTypeMap.planned) {
    return callback(false);
  }
  const selectedGroup = groups.find((group) => groupIdAccessor(group) === value);
  if (_notNil(hot) && _notNil(selectedGroup)) {
    const currentData = hot.getSourceData() as Array<AnimalRow>;
    const animalsInGroup = calculateAnimalsInGroup(selectedGroup, currentData, unselectedRows, groupIdAccessor);
    return callback(animalsInGroup <= selectedGroup.max_subjects);
  }
  return callback(true);
};

export const validateSex = (value: 'Male' | 'Female', callback: (valid: boolean) => void) => {
  callback(_notNil(value) && (value === 'Male' || value === 'Female'));
};

export const validateDob = (dobRequired: boolean, value: string, callback: (valid: boolean) => void) => {
  if (_isNotEmpty(value)) {
    const result = DateUtils.isValidDate(value) && !isAfter(new Date(value), new Date(DateUtils.dateNow()));
    return callback(result);
  }
  return callback(!dobRequired);
};

export const validateSpecies = (value: string, callback: (valid: boolean) => void, species: Array<SpeciesV1>) => {
  if (_isNotEmpty(value)) {
    return callback(species.some((s) => s.name === value));
  }
  return callback(true);
};

export const renderGroupContainer = (groups: Array<TreatmentGroup>, value: string | null) => {
  const selectedGroup = groups.find((group) => group.name === value) ?? '';
  const container = document.createElement('div');
  container.className = 'flex items-center pa2 justify-between';

  const contentContainer = document.createElement('div');
  contentContainer.className = 'flex items-center';

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (value && selectedGroup) {
    const swatch = document.createElement('div');
    swatch.className = 'dib w2 h1 br1 mr2';
    swatch.style.background = selectedGroup?.color ?? '#000000';

    const text = document.createElement('span');
    text.textContent = selectedGroup?.name ?? '';
    contentContainer.appendChild(swatch);
    contentContainer.appendChild(text);
  }

  container.appendChild(contentContainer);
  return container;
};

const groupCellRenderer = (groupOptions: Map<string, GroupOption>): BaseRenderer => {
  return (
    instance: Core,
    TD: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: any,
    cellProperties: CellProperties
  ) => {
    TD.innerHTML = '';
    const cellMeta = instance.getCellMeta(row, col);
    const readOnly = cellMeta.readOnly === true;
    TD.className = 'htMiddle';
    if (cellMeta.valid === false) {
      TD.classList.add('htInvalid');
    }
    if (readOnly) {
      TD.classList.add('htDimmed');
    }
    const container = document.createElement('div');
    container.classList.add('h-100', 'w-100');
    container.innerHTML = renderToStaticMarkup(
      <GroupCell option={groupOptions.get((value ?? '').trim())} readOnly={readOnly} />
    );
    TD.appendChild(container);
  };
};

export const getBaseColumns = (
  groups: Array<TreatmentGroup>,
  groupIdAccessor: (group: TreatmentGroup) => string,
  species: Array<SpeciesV1>,
  hotInstance: Handsontable | null,
  includeDob: boolean = false,
  dobRequired: boolean = true,
  editGroups: boolean = true,
  planType: PlanTypes,
  unselectedRows: Array<PlannedAnimalDefinition> = []
) => {
  const groupOptions = new Map<string, GroupOption>(
    groups.map((group) => {
      const value = groupIdAccessor(group);
      return [value, { value, label: group.name, color: group.color }];
    })
  );

  const baseColumns: any[] = [
    {
      data: 'plannedId',
      type: 'text',
      readOnly: false,
      allowInvalid: true,
      width: 150,
      validator: function (value: string, callback: (valid: boolean) => void) {
        validatePlannedAnimalId(value, unselectedRows, callback, hotInstance);
      },
      validationErrorMessage: () => 'ID is required and must be unique.',
    },
    {
      data: 'groupIdentifier',
      type: MultiSelectEditor.type,
      isMultiSelect: false,
      optionsMap: groupOptions,
      readOnly: !editGroups,
      width: 200,
      validator: function (value: string, callback: (valid: boolean) => void) {
        // Mapped animal definitions are included in group.animals_count, filter out here to avoid double counting
        validateGroupName(
          value,
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          unselectedRows.filter((a) => !a.animal_api_id),
          callback,
          hotInstance,
          groups,
          planType,
          groupIdAccessor
        );
      },
      validationErrorMessage: (value: string) =>
        _isEmpty(value) ? 'Planned animals must be assigned to a group.' : 'Group capacity exceeded.',
      renderer: groupCellRenderer(groupOptions),
    },
    {
      data: 'sex',
      type: 'dropdown',
      source: ['Male', 'Female'],
      allowInvalid: true,
      width: 110,
      validator: validateSex,
    },
  ];

  if (includeDob) {
    baseColumns.push({
      data: 'dob',
      type: 'date',
      dateFormat: 'YYYY-MM-DD',
      width: 150,
      required: dobRequired,
      validator: function (value: string, callback: (valid: boolean) => void) {
        validateDob(dobRequired, value, callback);
      },
      validationErrorMessage: () => 'Required in YYYY-MM-DD and must not be a future date.',
    });
  }

  baseColumns.push(
    {
      data: 'species',
      type: 'dropdown',
      width: 150,
      source: species.map((s) => s.name),
      validator: function (value: string, callback: (valid: boolean) => void) {
        validateSpecies(value, callback, species);
      },
      linkedValidationColumns: ['strain'],
    },
    {
      data: 'strain',
      type: 'dropdown',
      width: 150,
      source: [],
    }
  );

  return baseColumns;
};

export const getColumnData = (
  species: Array<SpeciesV1>,
  selectedMetadata: Array<MetadataField>,
  groups: Array<TreatmentGroup>,
  groupIdAccessor: (group: TreatmentGroup) => string,
  hotInstance: Handsontable | null,
  planType: PlanTypes,
  editGroups: boolean = true,
  dobRequired: boolean = true,
  unselectedRows: Array<PlannedAnimalDefinition> = []
) => {
  const includeDob = planType === PlanTypeMap.registered;
  const baseColumns = getBaseColumns(
    groups,
    groupIdAccessor,
    species,
    hotInstance,
    includeDob,
    dobRequired,
    editGroups,
    planType,
    unselectedRows
  );
  const colHeaders = includeDob
    ? dobRequired
      ? baseColHeadersWithDob
      : baseColHeadersWithOptionalDob
    : baseColHeaders;

  const metadataHeaders: string[] = [];
  const metadataColumns: any[] = [];
  selectedMetadata.forEach((metadata) => {
    const { columnHeader, columnSettings } = metadataToColumnSettingMapper(metadata);
    metadataHeaders.push(columnHeader);
    metadataColumns.push(columnSettings);
  });

  return {
    colHeaders: [...colHeaders.names, ...metadataHeaders],
    columns: [...baseColumns, ...metadataColumns],
  };
};

export const handleMetadataChange = (
  metadata: Array<MetadataField>,
  studyMetadata: Array<MetadataField>,
  setSelectedMetadata: (metadata: Array<MetadataField>) => void,
  setAvailableMetadata: (metadata: Array<MetadataField>) => void
) => {
  const addedMetadata = studyMetadata.filter((studyMeta) => {
    return !metadata.some((existingMeta) => existingMeta.id === studyMeta.id);
  });
  setSelectedMetadata(addedMetadata);
  setAvailableMetadata(metadata);
};

export const patchMetadata = (
  hotInstance: Handsontable | null,
  settings: CustomGridSettings,
  species: Array<SpeciesV1>
) => {
  if (_notNil(hotInstance)) {
    hotInstance.updateSettings(settings);
    const col = hotInstance.propToCol('species');
    const colData = hotInstance.getDataAtCol(col);
    colData.forEach((value: any, row: number) => {
      updateStrainSelectDropdown(hotInstance, species, row, value);
    });
    hotInstance.render();
  }
};

export type MetadataGlossaryItem = {
  id: number;
  api_id?: string;
  type: string;
  header?: string;
  title: string;
  field_type: MetadataFieldTypes;
  active?: boolean;
  in_use?: boolean;
  options?: Array<string>;
  read_only: boolean;
  slug: string;
  created_at?: string;
  required?: boolean;
};

export const mapMetadataResponse = (metadata: Array<MetadataGlossaryItem>) => {
  return metadata.map((meta) => ({
    id: meta.id,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    title: meta.title || '',
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    slug: meta.slug || '',
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    field_type: meta.field_type || 'text',
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    options: meta.options || [],
    active: meta.active !== undefined ? meta.active : true,
    glossary_id: meta.id,
    read_only: meta.read_only !== undefined ? meta.read_only : false,
    required: meta.required !== undefined ? meta.required : false,
    value: null,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    glossary_api_id: meta.api_id || `mgl_${meta.id}`,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    created_at: meta.created_at || new Date().toISOString(),
  })) as Array<MetadataField>;
};

const calculateAnimalsInGroup = (
  group: TreatmentGroup,
  animalData: Array<AnimalRow>,
  unselectedRows: Array<PlannedAnimalDefinition>,
  groupIdAccessor: (group: TreatmentGroup) => string
) => {
  return (
    (group.animals_count ?? 0) +
    animalData.filter((row) => row.groupIdentifier === groupIdAccessor(group)).length +
    unselectedRows.filter((row) => row.study_group_api_id === group.api_id).length
  );
};

export enum bannerMessageMap {
  baseMessage = 'The table is pre-populated based on the total number of animals specified in Groups and Treatments.',
  conflicts = 'There are conflicts in the animal data due to group changes. Reset the table to align animal data.',
  availableCapacity = 'The total group capacity exceeds the number of animals. Reset the table to align animal data.',
}
